import { supabase } from './supabase';
import { getSafeOrigin } from './webhookService';

/**
 * Publishes a post to Facebook using the Facebook Graph API
 * @param postId The ID of the post to publish
 * @param userId The ID of the user who is publishing the post
 * @returns A promise that resolves to the result of the publishing operation
 */
export async function publishToFacebook(postId: string, userId: string) {
  try {
    // Call the publish_to_facebook function in the database
    const { data, error } = await supabase.rpc('publish_to_facebook', {
      p_post_id: postId,
      p_user_id: userId
    });

    if (error) {
      console.error('Error publishing to Facebook:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in publishToFacebook:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

/**
 * Manually publishes a post using the manual_publish_post function
 * This will try to publish to Facebook if the platform is Facebook,
 * otherwise it will use a simulated publishing mechanism
 * @param taskId The ID of the task to publish
 * @param userId The ID of the user who is publishing the post
 * @returns A promise that resolves to the result of the publishing operation
 */
export async function manualPublishPost(taskId: string, userId: string) {
  try {
    // Call the manual_publish_post function in the database
    const { data, error } = await supabase.rpc('manual_publish_post', {
      p_task_id: taskId,
      p_user_id: userId
    });

    if (error) {
      console.error('Error in manual publish:', error);
      return { success: false, error: error.message };
    }

    // If data is null or undefined, return a default success response
    if (!data) {
      return { success: true };
    }

    // If data exists but doesn't have a success property, wrap it
    if (typeof data.success === 'undefined') {
      return { success: true, data };
    }

    return data;
  } catch (error) {
    console.error('Error in manualPublishPost:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

/**
 * Directly publishes a post (for testing)
 * This is a simulated publishing mechanism for testing
 * @param taskId The ID of the task to publish
 * @param userId The ID of the user who is publishing the post
 * @returns A promise that resolves to the result of the publishing operation
 */
export async function directPublishPost(taskId: string, userId: string) {
  try {
    // Call the direct_publish_post function in the database
    const { data, error } = await supabase.rpc('direct_publish_post', {
      p_task_id: taskId,
      p_user_id: userId
    });

    if (error) {
      console.error('Error in direct publish:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in directPublishPost:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}